// Canonical traveller-count helpers shared between the itinerary settings
// page (version-level) and the item traveller modal (item-level).
//
// Storage format: `{ people: [{ travelerTypeId, title }, ...] }` — one entry
// per person body. The UI aggregates by type so consultants edit counts
// instead of individual people; we expand back to `people[]` on save.

export type TravellerCount = {
  travelerTypeId: string;
  title: string;
  count: number;
};

type StoredPerson = { travelerTypeId?: unknown; title?: unknown };

const isStoredPerson = (v: unknown): v is StoredPerson =>
  typeof v === 'object' && v !== null;

// Reads raw travellers jsonb into aggregated counts.
// Handles: { people: [...] }, flat array [...], and the port's
// already-aggregated [{ id, title, count }] shape (maps `id` → `travelerTypeId`).
export const aggregateTravellers = (
  raw: unknown,
  typesById: Map<string, string>
): TravellerCount[] => {
  if (!raw || typeof raw !== 'object') return [];

  // Port shape: [{ id, title, count }] — already aggregated, `id` maps to travelerTypeId
  if (Array.isArray(raw)) {
    const first = raw[0];
    if (
      first &&
      typeof first === 'object' &&
      'id' in first &&
      'count' in first &&
      typeof (first as { id: unknown }).id === 'string' &&
      typeof (first as { count: unknown }).count === 'number'
    ) {
      return (raw as { id: string; title: string; count: number }[])
        .filter((t) => typeof t.id === 'string')
        .map((t) => ({
          travelerTypeId: t.id,
          title: typesById.get(t.id) ?? t.title ?? t.id,
          count: t.count,
        }));
    }
  }

  // Legacy: { people: [...] } or flat array of person objects
  const people: unknown[] = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as { people?: unknown }).people)
      ? ((raw as { people: unknown[] }).people as unknown[])
      : [];

  const byType = new Map<string, TravellerCount>();
  for (const person of people) {
    if (!isStoredPerson(person)) continue;
    const id =
      typeof person.travelerTypeId === 'string' ? person.travelerTypeId : null;
    if (!id) continue;
    const existing = byType.get(id);
    if (existing) {
      existing.count += 1;
      continue;
    }
    const title =
      typesById.get(id) ??
      (typeof person.title === 'string' ? person.title : '');
    byType.set(id, { travelerTypeId: id, title, count: 1 });
  }
  return Array.from(byType.values());
};

// Expands aggregated counts back to the canonical `people[]` jsonb shape.
export const buildPeoplePayload = (counts: TravellerCount[]) => ({
  people: counts.flatMap((entry) =>
    Array.from({ length: Math.max(0, entry.count) }, () => ({
      travelerTypeId: entry.travelerTypeId,
      title: entry.title,
    }))
  ),
});

export const filterPositiveTravellers = (
  travellers: TravellerCount[]
): TravellerCount[] => travellers.filter((t) => t.count > 0);

// Do the two edit states describe the same composition? Compared per type and
// ignoring row order / zero-count rows, because the editor renders a row for
// every traveller type whether or not it carries anyone.
//
// The traveller modal shows the item's EFFECTIVE travellers (its own, else its
// parent's, else the itinerary's), so a Save that changed nothing would
// otherwise copy an INHERITED composition onto the item as a manual override
// and detach it from every future cascade. Only a real edit may do that.
export const sameTravellerCounts = (
  a: TravellerCount[],
  b: TravellerCount[]
): boolean => {
  const index = (rows: TravellerCount[]): Map<string, number> => {
    const map = new Map<string, number>();
    for (const row of filterPositiveTravellers(rows)) {
      map.set(
        row.travelerTypeId,
        (map.get(row.travelerTypeId) ?? 0) + row.count
      );
    }
    return map;
  };
  const left = index(a);
  const right = index(b);
  if (left.size !== right.size) return false;
  for (const [id, count] of left) {
    if (right.get(id) !== count) return false;
  }
  return true;
};
